/**
 * CustomerInsightsFormRenderer Component
 * Renders a Customer Insights Journeys embed script inside the survey.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import type { AddressValue, Question, ResponseValue } from '../../../types';
import FormFieldInput from './FormFieldInput';

interface CustomerInsightsFormRendererProps {
  question: Question;
  selectedValues: Record<string, string | AddressValue>;
  onSelect: (value: Record<string, string | AddressValue>) => void;
  responses: Record<number, ResponseValue>;
  questions: Question[];
}

function isFormRecordResponse(response: ResponseValue): response is Record<string, string | number | boolean | null | undefined> {
  return typeof response === 'object'
    && response !== null
    && !Array.isArray(response)
    && !('answerId' in response)
    && !('answer' in response && 'score' in response && 'outcome' in response);
}

function isAddressValue(value: unknown): value is AddressValue {
  return value !== null
    && value !== undefined
    && typeof value === 'object'
    && 'addressLine1' in value
    && 'city' in value
    && 'postalCode' in value;
}

function CustomerInsightsFormRenderer({
  question,
  selectedValues,
  onSelect,
  responses,
  questions,
}: CustomerInsightsFormRendererProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const config = question.customerInsightsForm;
  const formFields = question.formFields ?? [];

  const prefilledValues = useMemo(() => {
    const values = { ...selectedValues };

    for (const mapping of config?.prefillMappings ?? []) {
      const targetKey = `field_${mapping.targetFieldId}`;
      if (values[targetKey]) continue;

      const sourceQuestion = questions.find((candidate) => candidate.id === mapping.sourceQuestionId);
      const sourceResponse = responses[mapping.sourceQuestionId];
      if (!sourceQuestion || sourceResponse === undefined) continue;

      if (mapping.sourceFieldId !== undefined && isFormRecordResponse(sourceResponse)) {
        const targetField = formFields.find((field) => field.id === mapping.targetFieldId);
        const sourceFieldValue = sourceResponse[`field_${mapping.sourceFieldId}`];
        if (targetField?.type === 'address' && isAddressValue(sourceFieldValue)) {
          values[targetKey] = sourceFieldValue;
        } else {
          values[targetKey] = String(sourceFieldValue ?? '');
        }
      } else if (mapping.sourceAnswerId !== undefined) {
        const answer = sourceQuestion.answers.find((candidate) => candidate.id === mapping.sourceAnswerId);
        const sourceAnswerValue = (
          typeof sourceResponse === 'object'
          && sourceResponse !== null
          && !Array.isArray(sourceResponse)
          && 'answerId' in sourceResponse
        )
          ? sourceResponse.answerId
          : sourceResponse;
        const sourceAnswerId = String(mapping.sourceAnswerId);
        const selected = Array.isArray(sourceAnswerValue)
          ? sourceAnswerValue.some((value) => String(value) === sourceAnswerId)
          : String(sourceAnswerValue) === sourceAnswerId;
        if (selected && answer) values[targetKey] = answer.text;
      } else if (typeof sourceResponse === 'string' || typeof sourceResponse === 'number') {
        values[targetKey] = String(sourceResponse);
      }
    }

    return values;
  }, [config?.prefillMappings, questions, responses, selectedValues]);

  useEffect(() => {
    if (JSON.stringify(prefilledValues) !== JSON.stringify(selectedValues)) {
      onSelect(prefilledValues);
    }
  }, [onSelect, prefilledValues, selectedValues]);

  useEffect(() => {
    if (!config || !containerRef.current) {
      return;
    }

    const safeMapping = config.mappingJson && config.mappingJson.trim() ? config.mappingJson.trim() : '[]';
    const selector = config.selector || '#form1';
    const formId = config.formId || '***Please fill***';
    const formApiUrl = config.formApiUrl || '***Please fill***';

    const generatedScript = config.script && config.script.trim()
      ? config.script.trim()
      : `
        d365mktformcapture.waitForElement(${JSON.stringify(selector)}).then(function(form) {
          const mappings = ${safeMapping};
          form.addEventListener('submit', function (event) {
            const serializedForm = d365mktformcapture.serializeForm(form, mappings);
            const payload = serializedForm.SerializedForm.build();

            const captureConfig = {
              FormId: ${JSON.stringify(formId)},
              FormApiUrl: ${JSON.stringify(formApiUrl)}
            };

            event.preventDefault();
            d365mktformcapture.submitForm(captureConfig, payload).catch(function (error) {
              console.error('Customer Insights form capture failed', error);
            });
          }, true);
        });
      `;

    containerRef.current.innerHTML = '';

    const appendCaptureScript = (): void => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.setAttribute('data-cj-form', String(question.id));
      script.text = generatedScript;
      containerRef.current?.appendChild(script);
    };

    if (config.bundleUrl?.trim()) {
      const bundle = document.createElement('script');
      bundle.src = config.bundleUrl.trim();
      bundle.async = false;
      bundle.setAttribute('data-cj-form-bundle', String(question.id));
      bundle.onload = appendCaptureScript;
      bundle.onerror = () => {
        console.error('Customer Insights FormCapture.bundle.js failed to load');
      };
      containerRef.current.appendChild(bundle);
    } else {
      appendCaptureScript();
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [config, question.id]);

  if (!config) {
    return <Box />;
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          {config.title || 'Customer Insights Form'}
        </Typography>

        {config.description && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {config.description}
          </Typography>
        )}

        <Box
          component="form"
          id={selectorId(config.selector || '#form1', question.id)}
          className={selectorClass(config.selector)}
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            onSelect(prefilledValues);
          }}
          sx={{
            width: '100%',
            minHeight: `${config.height || 700}px`,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fafafa',
            overflow: 'hidden',
            p: 2,
          }}
        >
          <Box sx={{ display: 'grid', gap: 2 }}>
            {formFields.map((field) => {
              const fieldKey = `field_${field.id}`;
              const value = prefilledValues[fieldKey] || '';
              return (
                <Box key={field.id}>
                  <Typography component="label" htmlFor={fieldKey} sx={{ display: 'block', mb: 0.75 }}>
                    {field.label}{field.isRequired ? ' *' : ''}
                  </Typography>
                  <FormFieldInput
                    field={field}
                    value={value}
                    inputId={fieldKey}
                    inputName={fieldKey}
                    onChange={(nextValue) => onSelect({ ...prefilledValues, [fieldKey]: nextValue })}
                  />
                </Box>
              );
            })}
            <Button type="submit" variant="contained">Submit</Button>
          </Box>
        </Box>
        <Box ref={containerRef} />
      </CardContent>
    </Card>
  );
}

function selectorId(selector: string, questionId: number): string | undefined {
  return selector.startsWith('#') ? selector.slice(1) : `ci-form-${questionId}`;
}

function selectorClass(selector: string | undefined): string | undefined {
  return selector?.startsWith('.') ? selector.slice(1) : undefined;
}

export default CustomerInsightsFormRenderer;
